/**
 * Was auf dem Board ausgewählt ist.
 *
 * Entweder Elemente oder Frames, nie beides zugleich: Die beiden verhalten sich
 * zu verschiedenen Dingen — ein Frame nimmt seinen Inhalt mit, ein Element
 * nicht — und eine gemischte Auswahl müsste bei jeder Aktion erklären, was sie
 * gerade meint.
 */
export type Selection = { kind: 'element' | 'frame'; ids: string[] } | null

export const isSelected = (
  selection: Selection,
  kind: 'element' | 'frame',
  id: string,
): boolean => selection?.kind === kind && selection.ids.includes(id)

/** Auswahl auf genau ein Objekt setzen. */
export const selectOne = (kind: 'element' | 'frame', id: string): Selection => ({ kind, ids: [id] })

/**
 * Ein Objekt zur Auswahl hinzunehmen oder daraus entfernen (Umschalt-Klick).
 *
 * Ein Klick auf eine andere Art wechselt die Auswahl komplett, statt zu
 * mischen — sonst entstünde genau die gemischte Auswahl, die es nicht geben
 * soll.
 */
export function toggleSelection(
  selection: Selection,
  kind: 'element' | 'frame',
  id: string,
): Selection {
  if (!selection || selection.kind !== kind) return selectOne(kind, id)
  if (selection.ids.includes(id)) {
    const rest = selection.ids.filter((candidate) => candidate !== id)
    return rest.length === 0 ? null : { kind, ids: rest }
  }
  return { kind, ids: [...selection.ids, id] }
}

/** Die ausgewählten Kennungen einer Art; leer, wenn etwas anderes ausgewählt ist. */
export const selectedIds = (selection: Selection, kind: 'element' | 'frame'): string[] =>
  selection?.kind === kind ? selection.ids : []

/**
 * Die Kennung, wenn **genau eine** dieser Art ausgewählt ist — sonst `null`.
 *
 * Für alles, was sich nur auf ein einzelnes Objekt anwenden lässt: die
 * Gestaltungsleiste eines Frames, das Ändern einer eingebetteten Adresse.
 */
export const soleId = (selection: Selection, kind: 'element' | 'frame'): string | null => {
  const ids = selectedIds(selection, kind)
  return ids.length === 1 ? (ids[0] ?? null) : null
}

/** Wie viele Objekte ausgewählt sind — unabhängig von der Art. */
export const selectionSize = (selection: Selection): number => selection?.ids.length ?? 0
